import { api } from "@/lib/api";
import { Invoice } from "@/types";

export interface InvoiceApiResponse {
  success: boolean;
  data: Invoice[];
  message?: string;
}

export interface InvoiceApiSingleResponse {
  success: boolean;
  data: Invoice;
  message?: string;
}

export interface InvoiceCreateInput {
  invoiceNumber: string;
  date: string; // ISO date string
  customerId: string;
  vehicleId: string;
  appointmentId?: string;
  subscriptionId?: string;
  items: Array<{
    name: string;
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  paymentStatus: "paid" | "pending" | "cancelled";
  paymentMethod?: string;
  notes?: string;
}

export interface InvoiceUpdateInput extends Partial<InvoiceCreateInput> {}

export interface RevenueChartData {
  date: string;      // "MMM dd" format
  revenue: number;
  dateISO?: string;  // Optional ISO date (can be ignored by frontend)
}

export interface RevenueChartApiResponse {
  success: boolean;
  data: RevenueChartData[];
  message?: string;
}

// Helper function to transform API response to frontend Invoice type
export function transformInvoiceFromApi(apiInvoice: any): Invoice {
  return {
    id: apiInvoice.id,
    invoiceNumber: apiInvoice.invoiceNumber || apiInvoice.invoice_number,
    date: new Date(apiInvoice.date || apiInvoice.createdAt),
    customerId: apiInvoice.customerId || apiInvoice.customer_id,
    vehicleId: apiInvoice.vehicleId || apiInvoice.vehicle_id,
    appointmentId: apiInvoice.appointmentId || apiInvoice.appointment_id,
    subscriptionId: apiInvoice.subscriptionId || apiInvoice.subscription_id,
    items: apiInvoice.items || [],
    subtotal: apiInvoice.subtotal || 0,
    discount: apiInvoice.discount || 0,
    platinumDiscount: apiInvoice.platinumDiscount || apiInvoice.platinum_discount || 0,
    tax: apiInvoice.tax || 0,
    total: apiInvoice.total || apiInvoice.amount || 0,
    paymentStatus: apiInvoice.paymentStatus || apiInvoice.payment_status || "pending",
    paymentMethod: apiInvoice.paymentMethod || apiInvoice.payment_method,
    notes: apiInvoice.notes,
  };
}

export const invoiceService = {
  async getAllInvoices(): Promise<Invoice[]> {
    try {
      const response = await api.get<InvoiceApiResponse>("/invoices");
      // Handle both array and object with data property
      const invoices = Array.isArray(response.data) 
        ? response.data 
        : response.data.data || [];
      return invoices.map(transformInvoiceFromApi);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      throw error;
    }
  },

  async getInvoiceById(id: string): Promise<Invoice> {
    try {
      const response = await api.get<InvoiceApiSingleResponse>(`/invoices/${id}`);
      return transformInvoiceFromApi(response.data.data || response.data);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      throw error;
    }
  },

  async createInvoice(invoice: InvoiceCreateInput): Promise<Invoice> {
    try {
      const response = await api.post<InvoiceApiSingleResponse>("/invoices", invoice);
      return transformInvoiceFromApi(response.data.data || response.data);
    } catch (error) {
      console.error("Error creating invoice:", error);
      throw error;
    }
  },

  async updateInvoice(id: string, invoice: InvoiceUpdateInput): Promise<Invoice> {
    try {
      const response = await api.put<InvoiceApiSingleResponse>(`/invoices/${id}`, invoice);
      return transformInvoiceFromApi(response.data.data || response.data);
    } catch (error) {
      console.error("Error updating invoice:", error);
      throw error;
    }
  },

  async deleteInvoice(id: string): Promise<void> {
    try {
      await api.delete(`/invoices/${id}`);
    } catch (error) {
      console.error("Error deleting invoice:", error);
      throw error;
    }
  },

  async getRevenueChartData(days: number = 7): Promise<RevenueChartData[]> {
    try {
      const response = await api.get<RevenueChartApiResponse>(
        `/dashboard/revenue-chart?days=${days}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching revenue chart data:", error);
      throw error;
    }
  },
};

